import { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const SUBJECTS = ["Math", "Reading", "Writing", "Science", "History", "Art", "Music", "Life Skills"];

function renderMarkdown(text) {
  if (!text) return null;
  // Very light markdown: bold + numbered list
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const html = line
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-stone-900">$1</strong>')
      .replace(/`(.+?)`/g, '<code class="bg-stone-100 px-1 rounded">$1</code>');
    return <p key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function Resources() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [subject, setSubject] = useState("Math");
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const s = await api.get("/students");
    setStudents(s.data);
    if (s.data.length && !studentId) setStudentId(s.data[0].id);
    const r = await api.get("/recommendations");
    setHistory(r.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const generate = async () => {
    if (!studentId) {
      toast.error("Add a student first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/recommendations/generate", { student_id: studentId, subject, topic });
      setResult(data);
      const r = await api.get("/recommendations");
      setHistory(r.data);
      toast.success("Fresh picks ready");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const student = students.find((s) => s.id === studentId);

  return (
    <div className="space-y-6" data-testid="ai-picks-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Resource picks</div>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Book & activity picks</h1>
        <p className="mt-2 text-stone-500">Recommends 5 resources tuned to each student's learning style, grade, and interests.</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label>Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="rec-student-select"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>
                {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · G{s.grade}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subject</Label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="rec-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Topic (optional)</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="fractions, ecosystems, WW2…" className="mt-2 rounded-xl h-11" data-testid="rec-topic-input" />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          {student && (
            <div className="text-sm text-stone-500">
              Tuned for <span className="text-stone-900 font-medium">{student.name}</span> — {student.learning_style} learner, Grade {student.grade}.
            </div>
          )}
          <Button onClick={generate} disabled={loading || !studentId} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-6" data-testid="generate-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Thinking…</> : <><Sparkles className="w-4 h-4 mr-2" /> Get 5 picks</>}
          </Button>
        </div>
      </div>

      {result && (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm fade-up" data-testid="rec-result">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600">
            <Sparkles className="w-3.5 h-3.5" /> Fresh picks
          </div>
          <div className="mt-3 space-y-2 text-stone-800">{renderMarkdown(result.content)}</div>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold mb-3">Previously generated</h2>
          <div className="space-y-3">
            {history.slice(0, 6).map((r) => (
              <details key={r.id} className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm" data-testid={`rec-history-${r.id}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <div>
                    <div className="font-medium text-stone-900">{r.subject}{r.topic ? ` · ${r.topic}` : ""}</div>
                    <div className="text-xs text-stone-500">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                </summary>
                <div className="mt-4 text-sm space-y-2">{renderMarkdown(r.content)}</div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
